/* ───────────────────────────────────────────────────────────────────────────
   FlashPro cutting-list planner.  Charl's rules, 19-20 Aug 2026.

   1. Coil width comes from the LOADED STOCK RECORD. Never assumed here - it is
      always passed in.
   2. If a girth is wider than the loaded coil, the coil is run LENGTHWISE:
      the girth runs ALONG the coil and the LENGTH runs across it, so the piece
      can be no longer than the coil is wide. Every plan says plainly which way
      it runs.
   3. Offcut numbering is not this file's business and is not touched.
   4. Work is planned LONGEST LENGTH DOWN, so what is left of a long run can
      still yield shorter flashings.
   5. Girths come from the drawn flashing. Nest as many as will fit across the
      width, within the length ordering.
   6. Offcuts are searched before coil - done by the caller, which passes any
      rack pieces in as `sheets`.
   7. Every pass is a PROPOSAL. Nothing is deducted here. The operator picks.
   8. Reversal is the caller's job; this file only describes what a pass uses.
   9. Nothing here touches the factory board.

   The rule that drove the rebuild: a pass runs ONE length of coil, so every
   strip in it must be accounted for down that whole length. The old planner
   set the run to the longest piece and silently stranded the remainder of the
   shorter strips - which is why the floor was told to cut lengths that did not
   add up.
   ─────────────────────────────────────────────────────────────────────────── */

const KERF_MM = 0;          // slitter kerf, if it ever matters
const SCRAP_MM = 100;       // under this across the width is scrap, not an offcut
/* ⚠️ Charl, 1 Sep: this was 300mm, so 400mm tails went on the RACK with proper offcut
   codes - "it's giving us these tiny pieces, and it's also giving us the offcut numbers for
   them, which is totally not needed."
   HIS RULE: "under 100mm it definitely goes to waste, and because we've got a minimum
   flashing length of one metre, I will also say anything under one metre." So a piece
   shorter than the shortest flashing anyone can order is not stock - it is waste. */
const SCRAP_LEN_MM = 1000;  // = MIN_ORDER_LEN below; kept literal because that is declared after this

function round2(n) { return Math.round(n * 100) / 100; }

/*  Charl, 20 Aug: nobody may order a flashing longer than the fold length, and
    nothing shorter than a metre. The input is limited AND warned, so it cannot
    be typed around. */
const MIN_ORDER_LEN = 1000;
const MAX_ORDER_LEN = 6400;

function checkLength(mm, coilWidth, lengthwise) {
  const n = Number(mm);
  if (!isFinite(n) || n <= 0) return { ok: false, value: null, msg: "Enter a length" };
  // a lengthwise run is capped by the coil, and that beats the normal maximum
  const cap = lengthwise && coilWidth ? Math.min(coilWidth, MAX_ORDER_LEN) : MAX_ORDER_LEN;
  if (n > cap) {
    return { ok: false, value: cap,
      msg: lengthwise
        ? "This is cut across the coil, so the longest piece possible is " + cap + "mm"
        : "Our maximum length is " + (MAX_ORDER_LEN / 1000).toFixed(1) + "m (" + MAX_ORDER_LEN + "mm)" };
  }
  // Charl, 20 Aug: on a lengthwise run THE COIL WINS. The 1m minimum is a
  // commercial rule; the coil width is physics. A 940 coil run lengthwise can
  // only give 940mm, so that is allowed rather than leaving the job impossible.
  const floor = lengthwise && coilWidth ? Math.min(MIN_ORDER_LEN, coilWidth) : MIN_ORDER_LEN;
  if (n < floor) {
    return { ok: false, value: floor,
      msg: "Our minimum length is " + (MIN_ORDER_LEN / 1000).toFixed(1) + "m (" + MIN_ORDER_LEN + "mm)" };
  }
  return { ok: true, value: n, msg: "" };
}

/* How many pieces of `len` fit in a run of `runLen`, and what is left over. */
function fitDown(runLen, len) {
  const n = Math.floor(runLen / len);
  return { pieces: n, leftover: round2(runLen - n * len) };
}

/*  Fill ONE strip of a given girth, down the whole run.
    This is the heart of Charl's rule 4: run the long piece, then keep cutting
    what is left of that same strip into shorter flashings of the same girth.
    Longest first, greedily, until nothing else fits. */
function fillStrip(runLen, items) {
  const cuts = [];
  let left = runLen;
  let moved = true;
  while (moved) {
    moved = false;
    for (const it of items) {                    // items are longest-first
      if (it.remaining <= 0) continue;
      if (it.length > left) continue;
      const n = Math.min(it.remaining, Math.floor(left / it.length));
      if (n < 1) continue;
      /* ⚠️ Charl, 2 Sep: "you don't put next to the offcut which flashing that offcut
         is for - the guys have to guess." The flashing NAME has been carried into the
         planner as orderRef all along and then thrown away right here, so nothing
         downstream could name the piece. Keep it. A cut can merge two items of the
         same length, so it is a LIST of names, not one.
         Purely additive - no planning decision reads it. */
      const found = cuts.find(c => c.length === it.length && c.split === !!it.split);
      if (found) {
        found.pieces += n;
        if (it.orderRef && found.refs.indexOf(it.orderRef) < 0) found.refs.push(it.orderRef);
      }
      else cuts.push({ length: it.length, pieces: n, ref: it,
                       refs: it.orderRef ? [it.orderRef] : [],
                       split: !!it.split, splitOf: it.splitOf, splitInto: it.splitInto,
                       turned: !!it.turned, trueGirth: it.trueGirth, trueLength: it.trueLength });
      it.remaining -= n;
      left = round2(left - n * it.length);
      moved = true;
      if (left <= 0) break;
    }
    if (left <= 0) break;
  }
  return { cuts, tailMm: round2(left) };
}

/*  Plan one pass across the width.
    The pass runs ONE length of coil. Every strip in it runs that whole length,
    so every strip must be filled or its tail declared - the old planner set the
    run to the longest piece and silently stranded the rest. */
function planPassAt(availWidth, work, maxKnives, runLen, ownSteel, chosenRun) {

  /* ⚠️ Charl, 31 Aug - this was sorting by WIDEST GIRTH only, which let rule 5 override
     rule 4. On his FlaxPod job the 393mm pieces (3750mm long) were taken ahead of the
     290mm pieces (4100mm long) purely because 393 > 290 - and the run was then set to
     4100 anyway, so every 393 strip threw a 350mm tail. 12.13% waste on that pass.

     His rule, twice stated and non-negotiable: "the system always needs to start with the
     biggest length first". So girths are ordered by the LONGEST PIECE each one still has
     outstanding, and width only settles a tie. Rule 5 nests WITHIN rule 4, not over it. */
  const longestFor = {};
  work.forEach(w => {
    if (w.remaining > 0) longestFor[w.girth] = Math.max(longestFor[w.girth] || 0, w.length);
  });
  const girths = [...new Set(work.map(w => w.girth))]
    .sort((a, b) => ((longestFor[b] || 0) - (longestFor[a] || 0)) || (b - a));

  const strips = [];
  let widthLeft = availWidth;
  let knives = 0;

  for (const girth of girths) {
    if (girth > widthLeft) continue;
    if (knives >= maxKnives) break;
    const mine = work.filter(w => w.girth === girth && w.remaining > 0)
                     .sort((a, b) => b.length - a.length);
    if (!mine.length) continue;

    /* ⚠️ Charl, 31 Aug: "why would it run a 4.1m piece when it could just run a 3750?"
       Nesting a girth into a run LONGER than its own longest piece strands a tail on every
       strip. On his FlaxPod job that turned one clean 620x4100 offcut into four awkward
       ones - 393x350, 227x4100, 393x250, 414x3750 - for exactly the same coil.

       So: do not START a girth in this pass if the run is longer than its longest piece
       AND the pass cannot finish that girth off anyway. If another pass is coming for it
       regardless, nothing is gained by stranding a tail here. A girth whose work FITS is
       still nested as before - that is rule 5 doing its job. */
    /* ownSteel = we are cutting a piece THIS ORDER ALREADY MADE. It costs no coil, so
       taking one strip off it is always worth doing even if another pass still follows.
       The skip below is about not wasting FRESH coil. Charl, 31 Aug. */
    const mineLongest = Math.max.apply(null, mine.map(m => m.length));
    if (!ownSteel && runLen > mineLongest) {
      const stripsHere = Math.min(Math.floor(widthLeft / girth), maxKnives - knives);
      const piecesLeft = mine.reduce((t, m) => t + m.remaining, 0);
      /* ⚠️ Charl, 21 Sep. This counted ONE piece per strip. On a run the operator has
         CHOSEN, a strip carries as many as fit end to end - his own case: two 500mm dry
         pans down a 1000mm run - and counting one meant the pass was skipped and the plan
         came back empty. Counted properly only for a chosen run, so no plan that was
         right yesterday moves today. */
      const perStrip = chosenRun
        ? Math.max(1, Math.min.apply(null, mine.filter(m => m.remaining > 0).map(m => fitDown(runLen, m.length) || 1)))
        : 1;
      const roomHere = stripsHere * perStrip;
      if (roomHere < piecesLeft) continue;   // another pass is coming - leave it whole
    }

    const roomForStrips = Math.min(Math.floor(widthLeft / girth), maxKnives - knives);
    for (let s = 0; s < roomForStrips; s++) {
      if (!mine.some(m => m.remaining > 0)) break;
      const { cuts, tailMm } = fillStrip(runLen, mine);
      if (!cuts.length) break;
      strips.push({
        girth,
        strips: 1,
        cuts: cuts.map(c => ({ length: c.length, pieces: c.pieces, refs: c.refs || [],
                               split: c.split, splitOf: c.splitOf, splitInto: c.splitInto,
                               turned: c.turned, trueGirth: c.trueGirth, trueLength: c.trueLength })),
        pieces: cuts.reduce((t, c) => t + c.pieces, 0),
        tailMm,
        tailUsable: tailMm >= SCRAP_LEN_MM
      });
      widthLeft = round2(widthLeft - girth - KERF_MM);
      knives += 1;
      if (widthLeft < SCRAP_MM || knives >= maxKnives) break;
    }
    if (widthLeft < SCRAP_MM || knives >= maxKnives) break;
  }

  return { runLen, strips, widthLeft: round2(widthLeft), knives };
}

/*  Choose the run length, rather than assuming it.
    A strip runs the whole length of the pass, so the run has to suit the mix
    being cut - running 4000mm for 2400mm pieces throws 1600mm away on every
    strip. Try the sensible candidates and keep the tightest.
    Rule 4 still holds: the longest outstanding piece MUST be cuttable, so any
    candidate shorter than it is rejected. */
/* what one hand cut that ends in scrap is worth against wasted area when picking a run. */
const SCRAP_TAIL_COST = 0.08;   // anywhere in 0.03-0.60 gives the same plan; measured
function planPass(availWidth, work, maxKnives, maxRun, minRun, exactRun) {
  const longest = Math.max.apply(null, work.map(w => w.length));
  const lengths = [...new Set(work.map(w => w.length))];

  /* ⚠️ Charl, 18 Sep: "Cutting pieces down to length by hand is time consuming, so when
     stuff comes off a coil it's always better to run what's required."
     This used to be seeded with maxRun - the coil's 6400mm cap - which is not a length
     ANYTHING on the job needs. Every other candidate below is a real sum of part lengths
     that a strip can carry end to end. maxRun was the one candidate that could only ever
     leave a tail on every strip, and it is what ran 5500mm of coil for a 3700mm part.
     A run is now always a length the job actually asks for. A longer run is still chosen
     when the extra carries ANOTHER piece - that is not surplus, that is two parts off one
     strip, and it is the whole point of the pair search. */
  const cands = new Set();
  lengths.forEach(L => {
    for (let m = 1; m * L <= maxRun; m++) cands.add(m * L);
    // pairs, so a 2400 and a 3600 can share a 6000 run
    lengths.forEach(L2 => {
      for (let a = 1; a * L <= maxRun; a++)
        for (let b = 1; a * L + b * L2 <= maxRun; b++) cands.add(a * L + b * L2);
    });
  });

  /* ⚠️ Charl, 21 Sep: "sometimes we do need to run it longer, but I want the ability to
     choose this and also be able to choose the length."
     A chosen length is USED, not scored against alternatives - he has decided. But a run
     has to be a real sum of part lengths, so a length nothing fits at is REFUSED with the
     nearest one that works, never silently ignored or quietly rounded. */
  /* ⚠️ Charl, 21 Sep: "it needs to respect our commands and run the pieces at that and work
     it out and then learn from it."
     So a length he asks for is RUN, even when it is not a tidy sum of part lengths - the
     surplus comes off as a tail, which is his call to make. It is only refused when the run
     is too short to cut anything at all, or longer than the fold length: then nothing can
     come off it, and the nearest length that does work is named. */
  if (exactRun) {
    const want = Number(exactRun) || 0;
    const shortest = Math.min.apply(null, work.filter(w => w.remaining > 0).map(w => w.length));
    const usable = [...cands].filter(r => r >= shortest && r <= maxRun).sort((a, b) => a - b);
    if (want > maxRun || want < shortest) {
      let near = null;
      usable.forEach(r => { if (near === null || Math.abs(r - want) < Math.abs(near - want)) near = r; });
      return { strips: [], runLen: 0, knives: 0, widthLeft: 0,
               refused: { asked: want, nearest: near, shortest, choices: usable.slice(0, 12) } };
    }
    const only = planPassAt(availWidth, work, maxKnives, want, false, true);
    if (!only.strips.length) {
      let near = null;
      usable.forEach(r => { if (near === null || Math.abs(r - want) < Math.abs(near - want)) near = r; });
      return { strips: [], runLen: 0, knives: 0, widthLeft: 0,
               refused: { asked: want, nearest: near, shortest, choices: usable.slice(0, 12) } };
    }
    return Object.assign(only, { runLen: want, chosenRun: true });
  }

  let best = null;
  for (const runLen of cands) {
    if (runLen < longest) continue;              // rule 4: the long piece must fit
    /* ⚠️ Charl, 1 Sep, RULE 17: "If you made the 6.2 into 6.4, then the offcut out of that
       could have worked for pass 3, and you didn't need pass 3 at all."
       A caller can ask for a LONGER run than this pass needs, betting that the extra tail
       kills a later pass. planCuttingList only keeps such a plan when it genuinely costs
       less coil - see the stretch search there. */
    if (minRun && runLen < minRun) continue;
    const trial = work.map(w => ({ ...w }));     // plan on a copy
    const p = planPassAt(availWidth, trial, maxKnives, runLen);
    if (!p.strips.length) continue;
    const areaRun = availWidth * runLen;
    const areaUsed = p.strips.reduce((t, s) =>
      t + s.girth * s.cuts.reduce((a, c) => a + c.pieces * c.length, 0), 0);
    const offcut = p.widthLeft >= SCRAP_MM ? p.widthLeft * runLen : 0;
    const wasted = areaRun - areaUsed - offcut;
    /* ⚠️ Charl, 18 Sep: "Cutting pieces down to length by hand is time consuming... if
       during the process an offcut is made that can be cut down to work, that's fine."
       A tail of a metre or more is fine - it becomes a real offcut and gets used. A tail
       UNDER a metre is a trip to the guillotine that produces scrap and nothing else.
       The score never knew the difference: it counted wasted AREA, so a run leaving four
       sub-metre tails scored the same as one leaving none.
       This steers the RUN LENGTH only. Refusing to nest those girths outright was measured
       first: it cut the scrap hard but cost 86 extra passes, and an extra setup is worse
       than a hand cut. */
    /* ⚠️ Charl, 18 Sep: "Why does it run 3900 for a 3700 piece? It should run at the
       length that was chosen."
       3900 was a pair sum - 2900 + 1000 - and a longer run IS right when a strip carries
       two pieces end to end. On that pass not one strip did: every strip held a single
       piece and every strip was left with a tail to cut off by hand. That is surplus, and
       it is exactly what he said must not happen.
       A run longer than the longest part is only allowed if at least ONE strip actually
       fills it. Otherwise the run is the longest part and nothing more. */
    if (runLen > longest + 1) {
      const fills = p.strips.some(st =>
        st.cuts.reduce((a, c) => a + c.pieces * c.length, 0) >= runLen - 1);
      if (!fills) continue;
    }
    const scrapTails = p.strips.filter(st => {
      const t = runLen - st.cuts.reduce((a, c) => a + c.pieces * c.length, 0);
      return t > 1 && t < SCRAP_LEN_MM;
    }).length;
    const score = wasted / areaRun + scrapTails * SCRAP_TAIL_COST;
    if (!best || score < best.score - 0.0001 ||
        (Math.abs(score - best.score) < 0.0001 && p.strips.reduce((t,s)=>t+s.pieces,0) > best.pieces)) {
      best = { score, runLen, pieces: p.strips.reduce((t,s)=>t+s.pieces,0) };
    }
  }
  if (!best) return { runLen: longest, strips: [], widthLeft: availWidth, knives: 0 };
  // replay the winner against the REAL work so `remaining` is decremented once
  return planPassAt(availWidth, work, maxKnives, best.runLen);
}

/*  RULE 6 - the rack is searched BEFORE any coil is touched.
    A rack piece is a fixed width AND a fixed length, unlike a coil, so it is
    matched on both. Best fit: the narrowest piece that still covers the girth,
    then the shortest that still covers the length, so a big sheet is not cut up
    for a small job. Material must match exactly - Colorcote is NOT COLORSTEEL. */
function useSheets(work, sheets, matKeyOf) {
  const used = [];
  if (!Array.isArray(sheets) || !sheets.length) return used;
  const avail = sheets
    .filter(s => s && s.status !== "used" && Number(s.width) > 0 && Number(s.length) > 0)
    .map(s => ({ ...s, widthLeft: Number(s.width), lengthMm: Number(s.length) }));

  // rule 4 holds here too - longest first
  const order = [...work].sort((a, b) => (b.length - a.length) || (b.girth - a.girth));

  for (const item of order) {
    while (item.remaining > 0) {
      const fits = avail.filter(s =>
        s.widthLeft >= item.girth &&
        s.lengthMm >= item.length &&
        // Refuse on anything unknown. A rack piece whose material we cannot
        // confirm is NOT offered - wrong steel is worse than opening a coil.
        (!matKeyOf || (s.material && item.material &&
          matKeyOf(s.material) === matKeyOf(item.material))) &&
        (!s.colour || (item.colour &&
          String(s.colour).trim().toLowerCase() === String(item.colour).trim().toLowerCase())));
      if (!fits.length) break;
      // narrowest that covers the girth, then shortest that covers the length
      fits.sort((a, b) => (a.widthLeft - b.widthLeft) || (a.lengthMm - b.lengthMm));
      const sheet = fits[0];
      const perStrip = Math.floor(sheet.lengthMm / item.length);
      const stripsFit = Math.floor(sheet.widthLeft / item.girth);
      const take = Math.min(item.remaining, perStrip * stripsFit);
      if (take < 1) break;
      const stripsUsed = Math.ceil(take / perStrip);
      /* ⚠️ Charl, 21 Sep: the printed sheet shows a rack piece with the same detail as a
         pass, so it needs the same facts: how many come off one strip, what is left across
         the width afterwards, and the tail down its length. */
      used.push({
        sheetId: sheet.id, code: sheet.code || null,
        girth: item.girth, length: item.length, pieces: take,
        strips: stripsUsed, perStrip,
        turned: !!item.turned, trueGirth: item.trueGirth || item.girth, trueLength: item.trueLength || item.length,
        widthLeft: round2(sheet.widthLeft - stripsUsed * item.girth),
        /* ⚠️ 21 Sep: the tail is what is left on the strip ACTUALLY cut, not on a full one.
           Two 1000mm pieces off a 4100mm strip leave 2100mm - a keeper - not 100mm of scrap.
           Full strips (when there is more than one) leave their own, smaller tail. */
        tailMm: round2(sheet.lengthMm - (take - (stripsUsed - 1) * perStrip) * item.length),
        tailFullMm: stripsUsed > 1 ? round2(sheet.lengthMm - perStrip * item.length) : null,
        fullStrips: Math.max(0, stripsUsed - 1),
        sheetWidth: Number(sheet.width), sheetLength: sheet.lengthMm
      });
      item.remaining -= take;
      sheet.widthLeft = round2(sheet.widthLeft - stripsUsed * item.girth);
      if (sheet.widthLeft < SCRAP_MM) {
        const k = avail.indexOf(sheet); if (k >= 0) avail.splice(k, 1);
      }
    }
  }
  return used;
}

/*  Build the whole plan.
    rows    : [{ girth, length, qty, orderRef }]  girths from the drawn flashing
    coil    : { width, id, code }                 THE LOADED STOCK RECORD (rule 1)
    opts    : { maxKnives, maxRun, sheets }       sheets = rack offcuts, tried first
*/
/* Charl, 31 Aug. Reusing an offcut only pays if it REMOVES a coil pass. On his FlaxPod job
   the 620x4100 could take one 393 strip - but the remaining two 393s still needed their own
   3750 run, so the coil bill was identical and one clean 620x4100 piece became four small
   ones. His words: "it's always better to have a bigger piece, you can do more with it."
   So the plan is worked out BOTH ways and the better one kept: least coil first, then the
   fewest leftovers, then the biggest single piece left on the rack. */
function planCuttingList(rows, coil, opts) {
  const o = opts || {};
  /* ── RULE 17: stretch a run to kill a later pass ──────────────────────────
     Charl, 1 Sep, from his FlaxPod job: pass 1 ran 6200 and a 305 strip cutting
     3500 threw a 2700 tail; pass 3 was a whole separate run of 2830 for one
     294mm strip. Stretch pass 1 to 6400 and that tail becomes 2900 - the 294 @
     2830 fits - so pass 3 disappears. 0.24 sq m of extra coil saves 3.40.

     HIS LIMIT, and it is the right one: only keep the stretch when it actually
     costs less coil. Everything else is a bet we do not need to take. */
  /* ⚠️ RULE 17 (Charl, 1 Sep) - the STRETCH half of it is REMOVED, 18 Sep, on his
     instruction and in his own words:
       "Cutting pieces down to length by hand is time consuming, so when stuff comes off a
        coil it's always better to run what's required. If during the process an offcut is
        made that can be cut down to work, that's fine, or if you use an offcut off the rack."
     The stretch scored itself on COIL AREA ALONE. It had no idea a hand cut costs time. On
     his Gull Grey job it ran pass 1 at 5500mm when the longest part on it was 3700 - 1800mm
     of surplus across four strips, four hand cuts, and four whole passes (2, 3, 4, 5) that
     existed only to use up what the stretch had manufactured.
     His 1 Sep example stretched 6200 to 6400 onto a tail that ALREADY existed. This one
     manufactured the tails. Off a coil we now run what is required.
     THE OTHER HALF OF RULE 17 STANDS, and it is the half that matters: a usable TAIL still
     goes in the pool, so an offcut that falls out on the way is still used before any coil
     is touched. That is exactly what he asked for. */
  if (!o.__noPool) {
    const withPool = planOnce(rows, coil, Object.assign({}, o, { __noPool: false, __forcePool: true }));
    const noPool = planOnce(rows, coil, Object.assign({}, o, { __noPool: true }));
    const score = (p) => {
      if (!p || !p.ok || !p.passes) return null;
      const coilArea = p.passes.filter(x => !x.fromOwnOffcut)
        .reduce((t, x) => t + x.coilWidth * x.runLen, 0);
      let pieces = 0, biggest = 0;
      p.passes.forEach(x => (x.leftovers || []).forEach(l => {
        if (l.consumed || l.kind === "scrap") return;
        pieces += 1;
        biggest = Math.max(biggest, (l.widthMm || 0) * (l.lengthMm || 0));
      }));
      return { coilArea, pieces, biggest };
    };
    const a = score(withPool), b = score(noPool);
    if (a && b) {
      if (b.coilArea < a.coilArea - 1000) return noPool;
      if (a.coilArea < b.coilArea - 1000) return withPool;
      // same coil: prefer fewer, bigger pieces on the rack
      if (b.pieces < a.pieces) return noPool;
      if (a.pieces < b.pieces) return withPool;
      return b.biggest > a.biggest ? noPool : withPool;
    }
    return a ? withPool : noPool;
  }
  return planOnce(rows, coil, o);
}

/* ── Longest run first ────────────────────────────────────────────────────────────
   ⚠️ Charl, 18 Sep: "Your cutting list always needs to run from the longest length to the
   shortest." His list went 3900, 1500, 1000, 1000, 2700, 2700, 2700, 1000, 2700, 3400,
   1000, 1300, 1300, 1300 - up and down all day, so the operator sets the machine long,
   short, short, long, short.
   Passes are ordered longest run to shortest now. The ONE thing that overrides it: a pass
   cut from an earlier pass's offcut must still come after the pass that made it, or the
   steel does not exist yet. So this takes the longest pass whose parent is already placed,
   repeatedly, and then renumbers - including every cross-reference, or "keep it at the
   machine for pass 3" would point at the wrong pass. */
function orderLongestFirst(passes) {
  const left = passes.slice();
  const out = [];
  const placed = new Set();
  while (left.length) {
    let pick = -1;
    for (let i = 0; i < left.length; i++) {
      const p = left[i];
      const parent = p.fromOwnOffcut ? p.offcutFromPass : null;
      if (parent && !placed.has(parent)) continue;      // its steel is not made yet
      if (pick < 0 || (left[i].runLen || 0) > (left[pick].runLen || 0)) pick = i;
    }
    if (pick < 0) pick = 0;                             // a cycle should be impossible; do not hang
    const p = left.splice(pick, 1)[0];
    placed.add(p.num);
    out.push(p);
  }
  /* renumber, and carry every reference with it */
  const map = {};
  out.forEach((p, i) => { map[p.num] = i + 1; });
  out.forEach((p, i) => {
    p.num = i + 1;
    if (p.offcutFromPass) p.offcutFromPass = map[p.offcutFromPass] || p.offcutFromPass;
    (p.leftovers || []).forEach(l => {
      if (l && l.consumedByPass) l.consumedByPass = map[l.consumedByPass] || l.consumedByPass;
      if (l && l.fromPass) l.fromPass = map[l.fromPass] || l.fromPass;
    });
    (p.strips || []).forEach(st => {
      if (st && st.fromPass) st.fromPass = map[st.fromPass] || st.fromPass;
    });
  });
  return out;
}

function planOnce(rows, coil, opts) {
  const o = opts || {};
  /* ⚠️ Charl, 1 Sep, RULE 19: "We need to be able to select each individual strip of what we
     want. And then when we recalculate, the whole cutting list needs to recalculate with
     what we've chosen."
     A piece the operator is taking off a rack offcut is simply NOT WORK ANY MORE. Take it
     out of the rows and plan what is left - the whole list re-shapes round the choice,
     which is exactly what he asked for. Each entry is {girth, length, qty}. */
  if (Array.isArray(o.exclude) && o.exclude.length) {
    rows = rows.map(r => ({ ...r }));
    o.exclude.forEach(x => {
      let want = Math.max(0, Number(x.qty) || 0);
      if (!want) return;
      rows.forEach(r => {
        if (!want) return;
        if (Math.round(Number(r.girth)) !== Math.round(Number(x.girth))) return;
        if (Math.round(Number(r.length)) !== Math.round(Number(x.length))) return;
        const take = Math.min(want, Number(r.qty) || 0);
        r.qty = (Number(r.qty) || 0) - take;
        want -= take;
      });
    });
    rows = rows.filter(r => (Number(r.qty) || 0) > 0);
    if (!rows.length) {
      return { ok: true, impossible: false, lengthwise: false, notice: null,
        coil: { width: Number(coil && coil.width) || 0, id: (coil && coil.id) || null },
        passes: [], totalPieces: 0, fromRack: [], short: [],
        allExcluded: true };
    }
  }
  const maxKnives = o.maxKnives || 5;
  const maxRun = o.maxRun || 6400;               // fold length cap (rule 2)
  const coilWidth = Number(coil && coil.width) || 0;

  if (!coilWidth) {
    return { ok: false, error: "No coil width - load a coil first", passes: [], lengthwise: false };
  }

  // ── rule 2: anything wider than the coil has to run lengthwise ────────────
  const wide = rows.filter(r => r.girth > coilWidth);
  const lengthwise = wide.length > 0;
  const maxLenLengthwise = coilWidth;

  const notice = lengthwise
    ? "Girth " + Math.max.apply(null, wide.map(r => r.girth)) + "mm is wider than the "
      + coilWidth + "mm coil, so the coil runs LENGTHWISE. The girth runs along the coil "
      + "and the length across it, so no piece can be longer than " + maxLenLengthwise + "mm."
    : "";

  // A flashing longer than the fold cap cannot be made in one piece, so it is
  // SPLIT into equal joining lengths - the old planner did this and the floor
  // relies on it. Flagged, so the cutting list says the piece is jointed.
  const expanded = [];
  rows.forEach(r => {
    const len = Number(r.length) || 0;
    if (len > maxRun && len > 0) {
      const n = Math.ceil(len / maxRun);
      expanded.push({ ...r, length: Math.ceil(len / n), qty: (Number(r.qty) || 0) * n,
                      split: true, splitOf: len, splitInto: n });
    } else expanded.push({ ...r });
  });

  // RULE 2, done properly. A lengthwise piece is TURNED: its girth runs ALONG the coil
  // and its length runs ACROSS it. So for planning the two swap over - the piece occupies
  // its LENGTH across the width, and the run is its GIRTH. Flagging it was not enough;
  // without the swap a 1385 girth simply would not fit a 1200 coil and planned nothing.
  /* ⚠️ Charl, 21 Sep: Benjamin wanted to run a dry pan at 500mm "because the coil width is
     1.2 metres - he wanted to use the material in its width rather than in its length ...
     even though we've got a minimum of a one metre run, if you use the coil in 1.2 you've
     achieved just that by running it at 500."
     He is right, and the app would not let him: a piece was only turned when it was WIDER
     than the coil. Turning a 500 girth x 1200 long piece runs the coil 500mm and takes the
     1200 across the width - 1000mm of coil for two pieces instead of 1200, and fewer knife
     sets. o.turnAcross is the operator saying so on the pass; it is never assumed. */
  const askedToTurn = !!o.turnAcross;
  const cannotTurn = askedToTurn
    ? expanded.filter(r => (Number(r.length) || 0) > coilWidth)
    : [];
  const work = expanded.map(r => {
    const g = Number(r.girth) || 0, L = Number(r.length) || 0;
    const turn = g > coilWidth || (askedToTurn && L <= coilWidth && L > 0);
    return {
    turned: turn,
    trueGirth: g, trueLength: L,
    girth: turn ? L : g,      // what it occupies ACROSS the coil
    length: turn ? g : L,     // what it occupies ALONG the coil
    remaining: Number(r.qty) || 0,
    qty: Number(r.qty) || 0,
    orderRef: r.orderRef || "",
    // MUST be carried through - the rack match reads these, and when they were
    // missing the guard saw `undefined` and let a Colorcote sheet onto a
    // COLORSTEEL job. Exactly the fault that started all this.
    split: !!r.split, splitOf: r.splitOf || null, splitInto: r.splitInto || null,
    material: r.material || coil.material || null,
    colour: r.colour || coil.colour || null,
    lengthwise: turn
  };}).filter(w => w.girth > 0 && w.length > 0 && w.remaining > 0);

  if (cannotTurn.length) {
    const worst = Math.max.apply(null, cannotTurn.map(r => Number(r.length) || 0));
    return { ok: false, impossible: false, lengthwise: false,
      coil: { width: coilWidth, id: (coil && coil.id) || null }, passes: [], totalPieces: 0,
      fromRack: [], short: [],
      refusedTurn: { longest: worst, coilWidth },
      code: "RUN-06",
      error: "RUN-06 - A " + worst + "mm piece cannot run across a " + coilWidth + "mm coil - it "
        + "is longer than the coil is wide. Only pieces up to " + coilWidth + "mm can be turned." };
  }

  // rule 4: longest length first, and hold that order throughout
  work.sort((a, b) => (b.length - a.length) || (b.girth - a.girth));

  // rule 6: the rack first, always. Whatever the rack covers never reaches the coil.
  const fromRack = useSheets(work, o.sheets, o.matKeyOf);

  let passes = [];   // reassigned once at the end, ordered longest run first
  let guard = 0;

  /* Charl, 26 Aug: "it doesn't actually look at how it can use the offcut that's been
     created to be used in the following flashings that are needed for that order. And
     that's exactly the reason why you start from the longer lengths."

     A pass across a 1200 coil with five knives at 100mm uses 500mm and leaves a
     700mm x run piece. The planner recorded that as an offcut and then pulled FRESH
     COIL for the next five - when the piece it had just made would have carried them.
     Pieces this order makes now go in a pool, and every following pass tries the pool
     BEFORE the coil. Widest first, so a piece is not wasted on a narrow strip. */
  const pool = [];      // { widthMm, lengthMm, fromPass }

  while (work.some(w => w.remaining > 0) && guard++ < 500) {
    const outstanding = work.filter(w => w.remaining > 0);

    // the pool first - steel we have already paid for (skipped when planning without it)
    let usedPool = null, poolPass = null;
    if (o.__noPool) { /* fall straight through to the coil */ } else
    pool.sort((a, b) => (b.widthMm - a.widthMm) || (b.lengthMm - a.lengthMm));
    /* ⚠️ Charl, 31 Aug: "we need to make sure that it works across all different run
       lengths, not just one type of run length." A piece we already own can be CUT DOWN -
       a 620x4100 offcut can serve a 3750mm job and keep a 620x350 remainder. Before this
       the pool always ran the piece at its FULL length, so an offcut whose length did not
       match the work was never used at all. Now the run length is chosen, up to the
       piece's own length, exactly as it is for a coil. */
    let poolRun = 0;
    for (let i = 0; !o.__noPool && i < pool.length; i++) {
      const piece = pool[i];
      let bestHere = null;
      const lens = [...new Set(outstanding.map(w => w.length))]
        .filter(L => L <= piece.lengthMm)
        .sort((a, b) => b - a);
      const cands = new Set([piece.lengthMm]);
      lens.forEach(L => { for (let m = 1; m * L <= piece.lengthMm; m++) cands.add(m * L); });
      for (const runLen of cands) {
        const trial = outstanding.map(w => ({ ...w }));
        const att = planPassAt(piece.widthMm, trial, maxKnives, runLen, true);
        if (!att.strips.length) continue;
        const used = att.strips.reduce((t, st) =>
          t + st.girth * st.cuts.reduce((a, c) => a + c.pieces * c.length, 0), 0);
        // prefer the run that puts the most steel to work, then the shortest run
        if (!bestHere || used > bestHere.used ||
            (used === bestHere.used && runLen < bestHere.runLen)) {
          bestHere = { used, runLen };
        }
      }
      if (bestHere) {
        usedPool = i; poolRun = bestHere.runLen;
        poolPass = planPassAt(piece.widthMm, outstanding, maxKnives, bestHere.runLen, true);
        break;
      }
    }
    if (poolPass) {
      const piece = pool[usedPool];
      pool.splice(usedPool, 1);
      /* ⚠️ STOCK: this piece is being cut up by THIS order, so it must not also land on
         the rack as an available offcut - that would count the same steel twice. Mark the
         leftover that created it, and the server skips it. */
      if (piece.ref) { piece.ref.consumed = true; piece.ref.consumedByPass = passes.length + 1; }
      const leftovers = [];
      const wLeft = poolPass.widthLeft;
      /* Two leftovers now, because the piece may have been cut SHORT of its own length:
         the width not used across the run, and the length remainder of the whole piece.
         Both go back in the pool - they are still steel this order owns. */
      if (wLeft >= SCRAP_MM) {
        const rest = { kind: "width", widthMm: wLeft, lengthMm: poolRun };
        leftovers.push(rest);
        pool.push({ widthMm: wLeft, lengthMm: poolRun, fromPass: passes.length + 1, ref: rest });
      } else if (wLeft > 0) leftovers.push({ kind: "scrap", widthMm: wLeft, lengthMm: poolRun });
      const lenLeft = piece.lengthMm - poolRun;
      if (lenLeft >= SCRAP_MM) {
        const rest2 = { kind: "width", widthMm: piece.widthMm, lengthMm: lenLeft };
        leftovers.push(rest2);
        pool.push({ widthMm: piece.widthMm, lengthMm: lenLeft, fromPass: passes.length + 1, ref: rest2 });
      } else if (lenLeft > 0) leftovers.push({ kind: "scrap", widthMm: piece.widthMm, lengthMm: lenLeft });
      poolPass.strips.forEach(st => {
        if (st.tailMm > 0) leftovers.push({ kind: st.tailUsable ? "tail" : "scrap",
                                            widthMm: st.girth, lengthMm: st.tailMm });
      });
      const areaHave = piece.widthMm * poolRun;
      const areaUse = poolPass.strips.reduce((t, s) =>
        t + s.girth * s.cuts.reduce((a, c) => a + c.pieces * c.length, 0), 0);
      passes.push({
        leftovers,
        num: passes.length + 1,
        runLen: poolRun,
        coilWidth: piece.widthMm,
        offcutWasLength: piece.lengthMm,   // so the docket can say what the piece was
        lengthwise,
        fromOwnOffcut: true,          // no coil is drawn for this pass
        offcutFromPass: piece.fromPass,
        strips: poolPass.strips.map(s => ({ girth: s.girth, strips: s.strips, cuts: s.cuts,
          pieces: s.pieces, tailMm: s.tailMm, tailUsable: s.tailUsable })),
        knives: poolPass.knives,
        wastePct: round2(((areaHave - areaUse) / areaHave) * 100),
        widthLeft: wLeft
      });
      continue;
    }

    /* Rule 17: the caller may ask for the FIRST coil pass to be stretched, betting the
       longer tail removes a later pass. Only the first - stretching several at once is a
       search with no end, and the win comes from the long first run. Charl, 1 Sep. */
    const coilPassesSoFar = passes.filter(p => !p.fromOwnOffcut).length;
    /* ⚠️ Charl, 21 Sep: a length HE chose for this coil pass (1-based, as the list numbers
       them). Keyed by coil pass so ticking an offcut cannot shift it onto another pass. */
    const chosen = (o.runLengths && Number(o.runLengths[String(coilPassesSoFar + 1)])) || 0;
    const pass = planPass(coilWidth, outstanding, maxKnives, maxRun,
      (coilPassesSoFar === 0 && o.__stretchFirst) ? o.__stretchFirst : 0, chosen);
    if (pass.refused) {
      /* ⚠️ 21 Sep, Benjamin's dry pan: a run too short to cut the piece ALONG the coil can
         be exactly right ACROSS it. If turning would make the length he asked for work, say
         so instead of just refusing - that is the difference between "no" and "no, but". */
      const canTurn = !askedToTurn && outstanding.some(w =>
        !w.turned && w.trueLength <= coilWidth && pass.refused.asked >= w.trueGirth);
      const code = canTurn ? "RUN-09" : "RUN-05";
      return { ok: false, impossible: false, lengthwise: false, coil: { width: coilWidth, id: (coil && coil.id) || null },
        passes: [], totalPieces: 0, fromRack: [], short: [],
        refusedRun: Object.assign({ pass: coilPassesSoFar + 1, canTurn }, pass.refused),
        code,
        error: code + " - Nothing fits at " + pass.refused.asked + "mm on pass " + (coilPassesSoFar + 1)
          + (canTurn
              ? " running ALONG the coil - but it works if you tick \'Run these across the coil\', because the piece is "
                + "no longer than the " + coilWidth + "mm width."
              : (pass.refused.nearest ? " - the nearest run that works is " + pass.refused.nearest + "mm" : "")) };
    }
    if (!pass.strips.length) break;              // nothing else fits - stop cleanly

    // fillStrip already decremented `remaining` on the work items it consumed

    const usedWidth = round2(pass.strips.reduce((t, s) => t + s.girth * s.strips, 0));
    const offcutWidth = pass.widthLeft >= SCRAP_MM ? pass.widthLeft : 0;
    const scrapWidth = pass.widthLeft < SCRAP_MM ? pass.widthLeft : 0;

    // honest waste: width not used, PLUS the tail left on every strip
    const areaRun = coilWidth * pass.runLen;
    const areaUsed = pass.strips.reduce((t, s) =>
      t + s.girth * s.cuts.reduce((a, c) => a + c.pieces * c.length, 0), 0);
    /* ⚠️ Charl, 18 Sep, off his Gull Grey list: pass 1 reported 50.83% waste while KEEPING
       four tails that fed passes 2, 3, 4 and 5 - its real scrap was 9.67%. Pass 15 reported
       0% on the same arithmetic, because its leftover happened to be a WIDTH rather than a
       tail. This only ever counted the leftover width as kept steel; every tail was called
       waste even when it was racked or handed to the next pass.
       Waste is what is SCRAPPED. A tail big enough to keep is not waste, wherever it sits. */
    const areaKeptTails = pass.strips.reduce((t, st) => {
      const tail = Number(st.tailMm) || 0;
      const keep = tail >= SCRAP_LEN_MM && st.girth >= SCRAP_MM && st.tailUsable !== false;
      return t + (keep ? st.girth * tail * (Number(st.strips) || 1) : 0);
    }, 0);
    const areaOffcut = offcutWidth * pass.runLen + areaKeptTails;
    const wastePct = round2(((areaRun - areaUsed - areaOffcut) / areaRun) * 100);

    // Everything this pass leaves behind, named. Width offcut down the whole run,
    // plus the TAIL on each strip - a 1200 x 240 piece is a real offcut for the rack,
    // and the old planner never mentioned it at all.
    const leftovers = [];
    if (offcutWidth) {
      leftovers.push({ kind: "width", widthMm: offcutWidth, lengthMm: pass.runLen });
      // and it goes in the pool, so the NEXT pass can use it instead of more coil
      pool.push({ widthMm: offcutWidth, lengthMm: pass.runLen, fromPass: passes.length + 1,
                  ref: leftovers[leftovers.length - 1] });
    }
    pass.strips.forEach(st => {
      if (st.tailMm > 0) {
        leftovers.push({
          kind: st.tailUsable ? "tail" : "scrap",
          widthMm: st.girth, lengthMm: st.tailMm
        });
        /* ⚠️ Charl, 1 Sep: a strip's TAIL never went in the pool - only the width
           leftover did. So the whole point of rule 17 was impossible: stretching a run
           makes a LONGER TAIL, and a tail the planner cannot see is a tail it cannot
           use. His case: pass 1's 305 strip leaves 2900mm, and the 294 @ 2830 of pass 3
           fits in it exactly. Now it can. */
        if (st.tailUsable) {
          pool.push({ widthMm: st.girth, lengthMm: st.tailMm,
                      fromPass: passes.length + 1,
                      ref: leftovers[leftovers.length - 1] });
        }
      }
    });
    if (scrapWidth > 0) leftovers.push({ kind: "scrap", widthMm: scrapWidth, lengthMm: pass.runLen });

    passes.push({
      leftovers,
      num: passes.length + 1,
      runLen: pass.runLen,
      coilWidth,
      lengthwise,
      strips: pass.strips.map(s => ({
        girth: s.girth, strips: s.strips, cuts: s.cuts, pieces: s.pieces,
        tailMm: s.tailMm, tailUsable: s.tailUsable
      })),
      usedWidth,
      offcutWidth,
      scrapWidth,
      knives: pass.knives,
      wastePct,
      chosenRun: !!pass.chosenRun,   /* 21 Sep: HE set this length - the screen says so */
      selected: false          // rule 7: nothing is used until the operator says so
    });
  }

  // Say WHY something could not be planned. "Short" on its own sent the floor a cutting
  // list with no passes and no explanation - which is what Charl saw on 20 Aug.
  const short = work.filter(w => w.remaining > 0).map(w => {
    let why = "could not be nested into this coil";
    if (w.turned && w.trueLength > coilWidth) {
      why = "girth " + w.trueGirth + "mm is wider than the " + coilWidth + "mm coil, so it must run "
          + "LENGTHWISE - and a lengthwise piece can be no longer than " + coilWidth + "mm, not "
          + w.trueLength + "mm. Redraw it shorter, or load a wider coil.";
    } else if (w.trueGirth > coilWidth && w.trueLength > coilWidth) {
      why = "neither the girth (" + w.trueGirth + "mm) nor the length (" + w.trueLength
          + "mm) fits the " + coilWidth + "mm coil.";
    } else if (w.length > maxRun) {
      why = "needs a run of " + w.length + "mm, longer than the " + maxRun + "mm fold length.";
    }
    return { girth: w.trueGirth, length: w.trueLength, short: w.remaining, why };
  });

  /* ⚠️ longest run first - Charl, 18 Sep. Done here, once, after every pass is planned,
     so nothing upstream has to care about order. */
  passes = orderLongestFirst(passes);

  /* ⚠️ 21 Sep. A plan with NO passes was still returned as ok:true, so the screen drew an
     empty cutting list and said nothing - which is exactly what "it just removed everything"
     looked like. If there was work and nothing could be planned, say so. */
  if (!passes.length && !fromRack.length && work.some(w => w.remaining > 0)) {
    const why = (short[0] && short[0].why) || "nothing could be planned onto this coil";
    return { ok: false, impossible: true, lengthwise,
      coil: { width: coilWidth, id: coil.id || null, code: coil.code || null },
      passes: [], fromRack: [], short, totalPieces: 0,
      code: "RUN-07",
      error: "RUN-07 - No pass could be planned - " + why };
  }

  return {
    ok: true,
    // a plan that cannot make everything is NOT a usable cutting list - say so loudly
    impossible: passes.length === 0 && fromRack.length === 0,
    lengthwise,
    maxLengthwiseLen: lengthwise ? maxLenLengthwise : null,
    notice,
    coil: { width: coilWidth, id: coil.id || null, code: coil.code || null },
    passes,
    fromRack,
    short,
    totalPieces: passes.reduce((t, p) => t + p.strips.reduce((s, x) => s + x.pieces, 0), 0)
      + fromRack.reduce((t, r) => t + r.pieces, 0),
    fromCoilPieces: passes.reduce((t, p) => t + p.strips.reduce((s, x) => s + x.pieces, 0), 0),
    fromRackPieces: fromRack.reduce((t, r) => t + r.pieces, 0),
    avgWastePct: passes.length
      ? round2(passes.reduce((t, p) => t + p.wastePct, 0) / passes.length) : 0
  };
}

/* ⚠️ Charl, 1 Oct: a drip edge in ANY LENGTHS - "it's just about metres they require, specific length doesn't matter" -
   with "nothing under 1m". metresToRows turns the metres still needed into EQUAL pieces, each between 1m and 6m (under the
   6.4m fold cap): 24m -> 4 x 6000, 25m -> 5 x 5000, 7m -> 2 x 3500, 0.4m -> 1 x 1000 (never a piece under a metre). */
const METRE_PIECE_MAX = 6000;
function metresToRows(girth, metres) {
  const total = Math.round((Number(metres) || 0) * 1000);
  if (!(total > 0) || !(Number(girth) > 0)) return [];
  const n = Math.max(1, Math.ceil(total / METRE_PIECE_MAX));
  const len = Math.max(MIN_ORDER_LEN, Math.ceil(total / n));
  return [{ girth: Math.ceil(Number(girth)), length: len, qty: n }];
}
/* what an offcut gives a metres flashing: whole strips of that girth across it, each the piece's length (capped at the fold) */
function metresFromOffcut(oc, girth) {
  const w = Number(oc && oc.width) || 0, L = Math.min(Number(oc && oc.length) || 0, MAX_ORDER_LEN), g = Number(girth) || 0;
  if (!(g > 0) || w < g || L < MIN_ORDER_LEN) return { strips: 0, length: 0, metres: 0 };
  const strips = Math.floor(w / g);
  return { strips, length: L, metres: Math.round(strips * L) / 1000 };
}

export { planCuttingList, planPass, fitDown, checkLength, metresToRows, metresFromOffcut,
         MIN_ORDER_LEN, MAX_ORDER_LEN, SCRAP_MM, SCRAP_LEN_MM };
